import { Component, Input, Output, EventEmitter, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { map } from 'rxjs/operators';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BaseTopAppBarComponent } from '../base-top-app-bar/base-top-app-bar.component';
import { TaskCounterBadgeComponent } from '../../../project/tasks/task-counter-badge/task-counter-badge.component';
import { ProjectSearchComponent } from '../project-search/project-search.component';
import { ChatStateService } from '../../../../services/chat-state.service';
import { AudioMuteService } from '../../../../shared/audio-mute.service';

/**
 * Project-specific top app bar that extends the base toolbar.
 * Provides project-specific functionality like hamburger menu, project title, and share button.
 *
 * Based on Angular component inheritance guide:
 * https://angular.dev/guide/components/inheritance
 */
@Component({
  selector: 'app-project-top-app-bar',
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatDividerModule,
    MatTooltipModule,
    BaseTopAppBarComponent,
    TaskCounterBadgeComponent,
    ProjectSearchComponent,
  ],
  template: `
    <app-base-top-app-bar
      [user$]="user$"
      [userRoleLabel]="'Logged in as'"
      (signOut)="onSignOut()"
      class="project-theme"
    >
      <!-- Hamburger menu (left of logo) -->
      <div slot="left-actions">
        @if (showProjectUI) {
          <button mat-icon-button aria-label="Toggle project navigation" (click)="onToggleDrawer()">
            <mat-icon>menu</mat-icon>
          </button>
        }
      </div>

      <!-- Project content area -->
      <div slot="content" class="project-content">
        <!-- Title and Menu Bar -->
        @if (showProjectUI) {
          <div class="title-menu-section">
            <!-- Project Title -->
            @if (currentProjectTitle) {
              <div class="project-title">{{ currentProjectTitle }}</div>
            }
            @if (!currentProjectTitle) {
              <div class="title-placeholder"></div>
            }
            <!-- Menu Bar -->
            <div class="menu-bar">
              <button mat-button [matMenuTriggerFor]="projectMenu" class="menu-item">Project</button>
              <button mat-button [matMenuTriggerFor]="viewMenu" class="menu-item">View</button>
            </div>
          </div>
        }
      </div>

      <!-- Search the whole project (Issue #2750), from the title to the icons. Ctrl+F stays exact
           find within the open file. Outside a project it shows nothing and only takes the room. -->
      <app-project-search
        slot="content"
        class="top-bar-search"
        [projectId]="showProjectUI ? currentProjectId : null"
      ></app-project-search>

      <!-- Project actions -->
      @if (showProjectUI) {
        <div slot="actions" class="project-actions-container">
          <!-- Desktop standalone actions (hidden on narrow screens) -->
          <div class="desktop-actions">
            @if ({ muted: audioMuteService.muted$ | async }; as audio) {
              <button
                mat-icon-button
                (click)="onToggleSpeakerMute()"
                [matTooltip]="audio.muted ? 'Unmute sound' : 'Mute all sound'"
                aria-label="Toggle global sound mute"
                class="speaker-mute-button"
                [class.speaker-muted]="audio.muted"
              >
                <mat-icon>{{ audio.muted ? 'volume_off' : 'volume_up' }}</mat-icon>
              </button>
            }
            @if (currentProjectId) {
              <button mat-icon-button (click)="onOpenShareDialog()" matTooltip="Share project" class="share-button">
                <mat-icon>person_add</mat-icon>
              </button>
            }
          </div>

          <!-- Mobile Overflow Actions Menu (displayed on narrow screens) -->
          <button
            mat-icon-button
            class="mobile-actions-menu-button"
            [matMenuTriggerFor]="projectActionsMenu"
            aria-label="More actions"
          >
            <mat-icon>more_vert</mat-icon>
          </button>
          <mat-menu #projectActionsMenu="matMenu">
            @if ({ muted: audioMuteService.muted$ | async }; as audio) {
              <button mat-menu-item (click)="onToggleSpeakerMute()">
                <mat-icon>{{ audio.muted ? 'volume_off' : 'volume_up' }}</mat-icon>
                <span>{{ audio.muted ? 'Unmute sound' : 'Mute all sound' }}</span>
              </button>
            }
            @if (currentProjectId) {
              <button mat-menu-item (click)="onOpenShareDialog()">
                <mat-icon>person_add</mat-icon>
                <span>Share project</span>
              </button>
            }
          </mat-menu>

          <app-task-counter-badge></app-task-counter-badge>
          @if ({ isOpen: isChatOpen$ | async }; as chat) {
            <button
              mat-icon-button
              (click)="onToggleChat()"
              [matTooltip]="chat.isOpen ? 'Close chat' : 'Open chat'"
              aria-label="Toggle chat"
              data-testid="chat-toggle"
              class="chat-toggle-button"
              [class.active]="chat.isOpen"
            >
              <mat-icon>chat</mat-icon>
            </button>
          }
        </div>
      }

      <!-- Additional user menu items for projects -->
      <div slot="user-menu">
        <button mat-menu-item (click)="onSwitchAccount()">
          <mat-icon>swap_horiz</mat-icon>
          <span>Switch Account</span>
        </button>
      </div>
    </app-base-top-app-bar>

    <!-- Project Menu -->
    <mat-menu #projectMenu="matMenu" class="project-menu">
      <button mat-menu-item (click)="onCreateNewProject()">
        <mat-icon>add</mat-icon>
        <span>New</span>
      </button>
      <button mat-menu-item (click)="onOpenProjectSelector()">
        <mat-icon>folder_open</mat-icon>
        <span>Open</span>
      </button>
      @if (currentProjectId) {
        <button mat-menu-item (click)="onOpenShareDialog()">
          <mat-icon>person_add</mat-icon>
          <span>Share</span>
        </button>
      }
      @if (currentProjectId) {
        <button mat-menu-item (click)="onOpenProjectCopyDialog()">
          <mat-icon>content_copy</mat-icon>
          <span>Make a copy</span>
        </button>
      }
      @if (currentProjectId) {
        <button mat-menu-item (click)="onOpenProjectDeleteDialog()" class="delete-menu-item">
          <mat-icon class="delete-icon">delete_forever</mat-icon>
          <span>Delete Project</span>
        </button>
      }
      <mat-divider></mat-divider>
      @if (currentProjectId) {
        <button mat-menu-item (click)="onOpenProjectSettings()">
          <mat-icon>settings</mat-icon>
          <span>Settings</span>
        </button>
      }
    </mat-menu>

    <!-- View Menu -->
    <mat-menu #viewMenu="matMenu" class="view-menu">
      @if (currentProjectId) {
        <button mat-menu-item (click)="onNavigateToTasks()">
          <mat-icon>task</mat-icon>
          <span>Tasks</span>
        </button>
      }
    </mat-menu>
  `,
  styleUrls: ['./project-top-app-bar.component.scss'],
})
export class ProjectTopAppBarComponent extends BaseTopAppBarComponent {
  private chatState = inject(ChatStateService);
  audioMuteService = inject(AudioMuteService);
  isChatOpen$ = this.chatState.state.pipe(map(state => state.isOpen));

  @Input() showProjectUI: boolean = false;
  @Input() currentProjectId: string | null = null;
  @Input() currentProjectTitle: string | null = null;

  @Output() toggleDrawer = new EventEmitter<void>();
  @Output() toggleChat = new EventEmitter<void>();
  @Output() openShareDialog = new EventEmitter<void>();
  @Output() override switchAccount = new EventEmitter<void>();
  @Output() createNewProject = new EventEmitter<void>();
  @Output() openProjectSelector = new EventEmitter<void>();
  @Output() openProjectCopyDialog = new EventEmitter<void>();
  @Output() openProjectDeleteDialog = new EventEmitter<void>();
  @Output() openProjectSettings = new EventEmitter<void>();
  @Output() navigateToTasks = new EventEmitter<void>();

  onToggleDrawer(): void {
    this.toggleDrawer.emit();
  }

  onToggleChat(): void {
    this.toggleChat.emit();
  }

  onOpenShareDialog(): void {
    this.openShareDialog.emit();
  }

  onSwitchAccount(): void {
    this.switchAccount.emit();
  }

  onCreateNewProject(): void {
    this.createNewProject.emit();
  }

  onOpenProjectSelector(): void {
    this.openProjectSelector.emit();
  }

  onOpenProjectCopyDialog(): void {
    this.openProjectCopyDialog.emit();
  }

  onOpenProjectDeleteDialog(): void {
    this.openProjectDeleteDialog.emit();
  }

  onOpenProjectSettings(): void {
    this.openProjectSettings.emit();
  }

  onNavigateToTasks(): void {
    this.navigateToTasks.emit();
  }

  onToggleSpeakerMute(): void {
    this.audioMuteService.toggle();
  }
}
